import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/authStore';

interface Notification {
  type: string;
  title: string;
  message: string;
  ticketId?: string;
  messageId?: string;
  priority?: string;
  category?: string;
  timestamp: string;
}

interface UseRealtimeNotificationsProps {
  onNotification?: (notification: Notification) => void;
  onNewTicket?: (ticket: any) => void;
  onNewMessage?: (message: any) => void;
  onStatusChange?: (ticket: any) => void;
  onBroadcast?: (broadcast: any) => void;
}

/**
 * Função helper para recuperar token do localStorage (mesma lógica do interceptor do axios)
 */
const getTokenFromStorage = (): string | null => {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    
    // PRIORIDADE 1: _pv001 (formato usado pelo sistema)
    try {
      const storage = localStorage.getItem('_pv001');
      if (storage) {
        const obj = JSON.parse(storage);
        const token = obj.t || obj.token || obj.accessToken || obj.authToken || null;
        if (token) {
          const tokenStr = String(token).trim();
          if (tokenStr && tokenStr.length > 0) {
            // Verificar formato básico de JWT (3 partes separadas por ponto)
            const tokenParts = tokenStr.split('.');
            if (tokenParts.length === 3) {
              return tokenStr;
            } else {
              console.error('❌ [SOCKET] Token no formato incorreto:', {
                tokenLength: tokenStr.length,
                parts: tokenParts.length
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ [SOCKET] Erro ao recuperar token de _pv001:', error);
    }
    
    // PRIORIDADE 2: auth-storage (Zustand)
    try {
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        const authData = JSON.parse(authStorage);
        const token = authData.state?.token || authData.token || authData.state?.accessToken || null;
        if (token) {
          const tokenStr = String(token).trim();
          if (tokenStr && tokenStr.length > 0) {
            const tokenParts = tokenStr.split('.');
            if (tokenParts.length === 3) {
              return tokenStr;
            }
          }
        }
      }
    } catch (error) {
      // Silenciar erro
    }
    
    // PRIORIDADE 3: useAuthStore (fallback)
    try {
      const storeToken = useAuthStore.getState().token;
      if (storeToken) {
        const tokenStr = String(storeToken).trim();
        if (tokenStr && tokenStr.length > 0) {
          const tokenParts = tokenStr.split('.');
          if (tokenParts.length === 3) {
            return tokenStr;
          }
        }
      }
    } catch (error) {
      // Silenciar erro
    }
    
    return null;
  } catch {
    return null;
  }
};

export const useRealtimeNotifications = ({
  onNotification,
  onNewTicket,
  onNewMessage,
  onStatusChange,
  onBroadcast
}: UseRealtimeNotificationsProps = {}) => {
  const socketRef = useRef<Socket | null>(null);
  
  // Buscar token do localStorage (mesma lógica do interceptor axios)
  const getToken = useCallback(() => {
    return getTokenFromStorage();
  }, []);

  const connect = useCallback(() => {
    const token = getToken();
    if (!token || socketRef.current?.connected) {
      if (!token) {
        console.warn('⚠️ [SOCKET] Token não encontrado, não conectando');
      }
      return;
    }

    // Determinar a URL do servidor baseada no ambiente
    const serverUrl = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001'
        : 'https://api-cms.cms20.online');

   
    socketRef.current = io(serverUrl, {
      // Polling primeiro costuma passar melhor por proxies/Cloudflare; CORS do servidor alinhado com Express.
      transports: ['polling', 'websocket'],
      timeout: 20000,
      forceNew: true,
      withCredentials: true
    });

    const socket = socketRef.current;

    // Eventos de conexão
    socket.on('connect', () => {
      // Buscar token novamente (pode ter mudado)
      const currentToken = getToken();
      if (currentToken) {
        // Validar formato do token antes de enviar
        const tokenStr = String(currentToken).trim();
        if (tokenStr && tokenStr.length > 0) {
          // Verificar formato básico de JWT (3 partes separadas por ponto)
          const tokenParts = tokenStr.split('.');
          if (tokenParts.length === 3) {
            socket.emit('authenticate', { token: tokenStr });
          } else {
            console.error('❌ [SOCKET] Token inválido - formato incorreto:', {
              tokenLength: tokenStr.length,
              parts: tokenParts.length
            });
          }
        } else {
          console.error('❌ [SOCKET] Token vazio ou inválido');
        }
      } else {
        console.error('❌ [SOCKET] Token não encontrado no localStorage');
      }
    });

    socket.on('authenticated', (data) => {

    });

    socket.on('auth_error', (error) => {
      // Logar erro de autenticação para debug
      console.error('❌ [SOCKET] Erro na autenticação:', error);
      // Não reconectar automaticamente se o token estiver inválido
      // O sistema tentará novamente quando o token for atualizado
    });

    socket.on('disconnect', (reason) => {

    });

    socket.on('connect_error', (error) => {
      // Erro de conexão em background - sistema continuará tentando
    });

    // Eventos de notificação
    socket.on('notification', (notification: Notification) => {
      
      // Chamar callback específico baseado no tipo
      switch (notification.type) {
        case 'new_ticket':
          onNewTicket?.(notification);
          break;
        case 'new_message':
          onNewMessage?.(notification);
          break;
        case 'ticket_status_changed':
          onStatusChange?.(notification);
          break;
        case 'admin_broadcast':
          onBroadcast?.(notification);
          break;
        default:
          onNotification?.(notification);
      }
    });

    // Eventos de erro
    socket.on('error', (error) => {
      // Erro em background - sistema continuará funcionando
    });

  }, [getToken, onNotification, onNewTicket, onNewMessage, onStatusChange, onBroadcast]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
       socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const isConnected = useCallback(() => {
    return socketRef.current?.connected || false;
  }, []);

  const getConnectionState = useCallback(() => {
    if (!socketRef.current) return 'disconnected';
    return socketRef.current.connected ? 'connected' : 'connecting';
  }, []);

  // Conectar quando o token estiver disponível
  useEffect(() => {
    const currentToken = getToken();
    if (currentToken) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup na desmontagem
    return () => {
      disconnect();
    };
  }, [getToken, connect, disconnect]);

  // Reconectar automaticamente em caso de perda de conexão
  useEffect(() => {
    if (!socketRef.current) return;

    const socket = socketRef.current;

    const handleReconnect = () => {
      // Tentando reconectar em background
      setTimeout(() => {
        if (!socket.connected) {
          connect();
        }
      }, 5000);
    };

    socket.on('disconnect', handleReconnect);

    return () => {
      socket.off('disconnect', handleReconnect);
    };
  }, [connect]);

  return {
    connect,
    disconnect,
    isConnected,
    getConnectionState,
    socket: socketRef.current
  };
};
